import React, { useEffect, useMemo, useState } from "react";
import { motion } from "framer-motion";
import { Star, Loader2, CheckCircle2, MessageSquareQuote, ExternalLink } from "lucide-react";
import { SEO } from "../components/SEO";
import { BIZ, GOOGLE_REVIEW_URL } from "../lib/constants";
import { api } from "../lib/api";

const SOURCES = [
    { key: "all", label: "All" },
    { key: "google", label: "Google" },
    { key: "facebook", label: "Facebook" },
    { key: "onsite", label: "On-site" },
];

const StarRow = ({ value = 0, size = 14, interactive = false, onChange, testid }) => (
    <div
        className="flex gap-1"
        role={interactive ? "radiogroup" : "img"}
        aria-label={interactive ? "Rate your experience" : `${value} out of 5 stars`}
        data-testid={testid}
    >
        {[1, 2, 3, 4, 5].map((n) => {
            const filled = n <= value;
            const Btn = interactive ? "button" : "span";
            return (
                <Btn
                    key={n}
                    type={interactive ? "button" : undefined}
                    onClick={interactive ? () => onChange(n) : undefined}
                    aria-label={interactive ? `${n} star${n > 1 ? "s" : ""}` : undefined}
                    role={interactive ? "radio" : undefined}
                    aria-checked={interactive ? value === n : undefined}
                    style={{
                        background: "transparent",
                        border: "none",
                        padding: interactive ? 4 : 0,
                        cursor: interactive ? "pointer" : "default",
                        lineHeight: 0,
                    }}
                    data-testid={interactive ? `review-star-${n}` : undefined}
                >
                    <Star
                        size={size}
                        fill={filled ? "var(--ls-gold)" : "transparent"}
                        stroke="var(--ls-gold)"
                        strokeWidth={1.5}
                    />
                </Btn>
            );
        })}
    </div>
);

const Distribution = ({ dist = {}, total = 0 }) => {
    const rows = [5, 4, 3, 2, 1];
    return (
        <div className="flex flex-col gap-2" data-testid="reviews-distribution">
            {rows.map((n) => {
                const count = dist[n] || dist[String(n)] || 0;
                const pct = total ? Math.round((count / total) * 100) : 0;
                return (
                    <div key={n} className="flex items-center gap-3" data-testid={`reviews-dist-row-${n}`}>
                        <span
                            className="text-xs font-medium"
                            style={{ color: "var(--ls-text-secondary)", width: 28 }}
                        >
                            {n}★
                        </span>
                        <div
                            style={{
                                flex: 1,
                                height: 6,
                                background: "var(--ls-border)",
                                borderRadius: 999,
                                overflow: "hidden",
                            }}
                        >
                            <div
                                style={{
                                    width: `${pct}%`,
                                    height: "100%",
                                    background: "var(--ls-gold)",
                                    transition: "width 0.4s ease",
                                }}
                            />
                        </div>
                        <span
                            className="text-xs"
                            style={{ color: "var(--ls-text-muted)", width: 32, textAlign: "right" }}
                        >
                            {count}
                        </span>
                    </div>
                );
            })}
        </div>
    );
};

export default function Reviews() {
    const [summary, setSummary] = useState(null);
    const [reviews, setReviews] = useState([]);
    const [loading, setLoading] = useState(true);
    const [source, setSource] = useState("all");
    const [ratingFilter, setRatingFilter] = useState(0); // 0 = any

    // Submission form state
    const [form, setForm] = useState({
        author_name: "",
        author_location: "",
        rating: 0,
        title: "",
        body: "",
        website: "",
    });
    const [submitState, setSubmitState] = useState("idle");
    const [submitMessage, setSubmitMessage] = useState("");
    const [formErrors, setFormErrors] = useState({});

    useEffect(() => {
        api.reviewsSummary().then(setSummary).catch(() => setSummary(null));
    }, []);

    useEffect(() => {
        setLoading(true);
        const params = { limit: 100, status: "approved" };
        if (source !== "all") params.source = source;
        if (ratingFilter) params.rating = ratingFilter;
        api.reviews(params)
            .then((d) => setReviews(d.items || []))
            .catch(() => setReviews([]))
            .finally(() => setLoading(false));
    }, [source, ratingFilter]);

    const jsonLd = useMemo(() => {
        if (!summary || !summary.count) return null;
        return {
            "@context": "https://schema.org",
            "@type": "LocalBusiness",
            "@id": `${BIZ.siteUrl}/#business`,
            name: BIZ.name,
            url: `${BIZ.siteUrl}/reviews`,
            telephone: BIZ.phone,
            address: {
                "@type": "PostalAddress",
                streetAddress: BIZ.address.line1,
                addressLocality: BIZ.address.city,
                addressRegion: BIZ.address.state,
                postalCode: BIZ.address.zip,
                addressCountry: BIZ.address.country,
            },
            aggregateRating: {
                "@type": "AggregateRating",
                ratingValue: String(summary.average),
                reviewCount: summary.count,
                bestRating: "5",
                worstRating: "1",
            },
            review: reviews.slice(0, 10).map((r) => ({
                "@type": "Review",
                author: { "@type": "Person", name: r.author_name },
                datePublished: (r.published_at || r.created_at || "").slice(0, 10),
                reviewBody: r.body,
                name: r.title || undefined,
                reviewRating: {
                    "@type": "Rating",
                    ratingValue: String(r.rating),
                    bestRating: "5",
                    worstRating: "1",
                },
            })),
        };
    }, [summary, reviews]);

    const validate = () => {
        const errs = {};
        if (!form.author_name.trim() || form.author_name.trim().length < 2)
            errs.author_name = "Please add your name (First name + last initial).";
        if (!form.rating) errs.rating = "Tap a star to rate.";
        if (!form.body.trim() || form.body.trim().length < 20)
            errs.body = "Reviews need to be at least 20 characters.";
        return errs;
    };

    const submitReview = async (e) => {
        e.preventDefault();
        const errs = validate();
        setFormErrors(errs);
        if (Object.keys(errs).length) return;
        setSubmitState("loading");
        try {
            const data = await api.submitReview(form);
            setSubmitState("success");
            setSubmitMessage(data.message || "Thanks — your review is pending approval.");
        } catch (err) {
            setSubmitState("error");
            setSubmitMessage(
                err?.data?.detail?.[0]?.msg ||
                    err?.message ||
                    "Could not submit. Please try again."
            );
        }
    };

    return (
        <>
            <SEO
                title={`Reviews${summary ? ` — ${summary.average}★ from ${summary.count} customers` : ""}`}
                description={`Read customer reviews of Liquor Studio, Clinton MO. ${summary ? `${summary.average} stars from ${summary.count} verified reviews.` : ""} Bourbon, wine, craft beer and curated spirits in Henry County.`}
                path="/reviews"
                jsonLd={jsonLd}
            />

            {/* Header */}
            <section
                style={{ paddingTop: 160, paddingBottom: 64, borderBottom: "1px solid var(--ls-border)" }}
                data-testid="reviews-header"
            >
                <div className="ls-container">
                    <motion.div
                        initial={{ opacity: 0, y: 16 }}
                        animate={{ opacity: 1, y: 0 }}
                        transition={{ duration: 0.6 }}
                        className="max-w-3xl"
                    >
                        <span className="eyebrow">Reviews</span>
                        <h1
                            className="font-display mt-5 text-white"
                            style={{
                                fontSize: "clamp(44px, 7vw, 84px)",
                                fontWeight: 500,
                                lineHeight: 1.02,
                                letterSpacing: "-0.02em",
                            }}
                        >
                            What Clinton<br />
                            <em style={{ fontStyle: "italic", color: "var(--ls-gold)", fontWeight: 400 }}>
                                is saying.
                            </em>
                        </h1>
                        <p
                            className="mt-6"
                            style={{
                                color: "var(--ls-text-secondary)",
                                fontSize: 18,
                                lineHeight: 1.7,
                                maxWidth: 620,
                            }}
                        >
                            Real customers. Real bottles. From bourbon hunters in Sedalia to Truman Lake
                            weekenders, here's what people say about Liquor Studio.
                        </p>
                    </motion.div>
                </div>
            </section>

            {/* Summary + Submit CTA */}
            <section className="section-y" data-testid="reviews-summary-section">
                <div className="ls-container">
                    <div className="grid grid-cols-1 lg:grid-cols-12 gap-10">
                        {/* Score block */}
                        <article
                            className="lg:col-span-5"
                            style={{
                                background: "var(--ls-panel)",
                                border: "1px solid var(--ls-border)",
                                borderRadius: 8,
                                padding: 40,
                            }}
                            data-testid="reviews-summary-card"
                        >
                            {summary ? (
                                <>
                                    <div className="flex items-end gap-4">
                                        <span
                                            className="font-display text-white"
                                            style={{
                                                fontSize: 80,
                                                fontWeight: 500,
                                                lineHeight: 1,
                                                letterSpacing: "-0.04em",
                                            }}
                                            data-testid="reviews-avg-rating"
                                        >
                                            {summary.average}
                                        </span>
                                        <div className="flex flex-col gap-2 pb-2">
                                            <StarRow value={Math.round(summary.average)} size={20} testid="reviews-summary-stars" />
                                            <span style={{ color: "var(--ls-text-muted)", fontSize: 13 }} data-testid="reviews-count">
                                                Based on {summary.count} review{summary.count === 1 ? "" : "s"}
                                            </span>
                                        </div>
                                    </div>
                                    <div className="mt-8">
                                        <Distribution dist={summary.distribution} total={summary.count} />
                                    </div>
                                </>
                            ) : (
                                <div className="flex items-center gap-3">
                                    <Loader2 size={18} className="animate-spin" style={{ color: "var(--ls-gold)" }} />
                                    <span style={{ color: "var(--ls-text-secondary)" }}>Loading review summary…</span>
                                </div>
                            )}
                        </article>

                        {/* CTA block */}
                        <div
                            className="lg:col-span-7"
                            style={{
                                background: "linear-gradient(180deg, var(--ls-panel) 0%, #0e0e0e 100%)",
                                border: "1px solid var(--ls-border)",
                                borderRadius: 8,
                                padding: 40,
                            }}
                        >
                            <MessageSquareQuote size={28} style={{ color: "var(--ls-gold)" }} />
                            <h2
                                className="font-display mt-4 text-white"
                                style={{ fontSize: 32, fontWeight: 500, lineHeight: 1.15 }}
                            >
                                Already a regular?
                            </h2>
                            <p
                                className="mt-3"
                                style={{ color: "var(--ls-text-secondary)", fontSize: 16, lineHeight: 1.6 }}
                            >
                                Drop a review below — we read every one. Or share on Google to help neighbors find us.
                            </p>
                            <div className="mt-6 flex flex-wrap gap-3">
                                <a
                                    href="#leave-a-review"
                                    className="btn-primary"
                                    style={{ fontSize: 13, padding: "12px 22px" }}
                                    data-testid="reviews-scroll-to-form"
                                >
                                    Write a Review
                                </a>
                                <a
                                    href={`https://www.google.com/maps/search/?api=1&query=${encodeURIComponent(BIZ.address.full)}`}
                                    target="_blank"
                                    rel="noopener noreferrer"
                                    className="btn-outline-gold"
                                    style={{ fontSize: 13, padding: "12px 22px" }}
                                    data-testid="reviews-google-cta"
                                >
                                    Review on Google <ExternalLink size={13} />
                                </a>
                            </div>
                        </div>
                    </div>
                </div>
            </section>

            {/* Filters + List */}
            <section style={{ paddingBottom: 96 }} data-testid="reviews-list-section">
                <div className="ls-container">
                    {/* Source tabs */}
                    <div
                        className="flex flex-wrap items-center justify-between gap-4 mb-8"
                        style={{
                            borderBottom: "1px solid var(--ls-border)",
                            paddingBottom: 16,
                        }}
                    >
                        <div className="flex flex-wrap gap-2" role="tablist" aria-label="Filter by source">
                            {SOURCES.map((s) => {
                                const active = source === s.key;
                                return (
                                    <button
                                        key={s.key}
                                        role="tab"
                                        aria-selected={active}
                                        onClick={() => setSource(s.key)}
                                        className="text-sm font-medium"
                                        style={{
                                            padding: "8px 16px",
                                            borderRadius: 999,
                                            border: `1px solid ${active ? "var(--ls-gold)" : "var(--ls-border-strong)"}`,
                                            background: active ? "rgba(245,176,39,0.12)" : "transparent",
                                            color: active ? "var(--ls-gold)" : "var(--ls-text-secondary)",
                                            transition: "all 0.2s ease",
                                            cursor: "pointer",
                                        }}
                                        data-testid={`reviews-tab-${s.key}`}
                                    >
                                        {s.label}
                                    </button>
                                );
                            })}
                        </div>

                        {/* Rating filter */}
                        <div className="flex items-center gap-2 flex-wrap">
                            <span className="text-xs" style={{ color: "var(--ls-text-muted)" }}>
                                Rating:
                            </span>
                            {[0, 5, 4, 3, 2, 1].map((n) => {
                                const active = ratingFilter === n;
                                return (
                                    <button
                                        key={n}
                                        onClick={() => setRatingFilter(n)}
                                        className="text-xs font-medium"
                                        style={{
                                            padding: "6px 12px",
                                            borderRadius: 999,
                                            border: `1px solid ${active ? "var(--ls-gold)" : "var(--ls-border-strong)"}`,
                                            background: active ? "rgba(245,176,39,0.12)" : "transparent",
                                            color: active ? "var(--ls-gold)" : "var(--ls-text-secondary)",
                                            cursor: "pointer",
                                        }}
                                        data-testid={`reviews-rating-${n}`}
                                    >
                                        {n === 0 ? "Any" : `${n}★`}
                                    </button>
                                );
                            })}
                        </div>
                    </div>

                    {loading ? (
                        <div
                            className="text-center py-20"
                            style={{ color: "var(--ls-text-secondary)" }}
                            data-testid="reviews-loading"
                        >
                            <Loader2 size={20} className="animate-spin inline-block mr-2" />
                            Loading reviews…
                        </div>
                    ) : reviews.length === 0 ? (
                        <p
                            className="text-center py-20"
                            style={{ color: "var(--ls-text-muted)" }}
                            data-testid="reviews-empty"
                        >
                            No reviews match this filter yet.
                        </p>
                    ) : (
                        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6" data-testid="reviews-grid">
                            {reviews.map((r, i) => (
                                <motion.figure
                                    key={r.id || i}
                                    initial={{ opacity: 0, y: 16 }}
                                    whileInView={{ opacity: 1, y: 0 }}
                                    viewport={{ once: true }}
                                    transition={{ duration: 0.4, delay: Math.min(i * 0.04, 0.4) }}
                                    style={{
                                        background: "var(--ls-panel)",
                                        border: "1px solid var(--ls-border)",
                                        borderRadius: 8,
                                        padding: 28,
                                        display: "flex",
                                        flexDirection: "column",
                                    }}
                                    data-testid={`review-card-${i}`}
                                >
                                    <div className="flex items-center justify-between mb-4">
                                        <StarRow value={r.rating} size={14} />
                                        <span
                                            className="text-xs"
                                            style={{
                                                color: "var(--ls-text-muted)",
                                                textTransform: "capitalize",
                                                letterSpacing: "0.06em",
                                            }}
                                        >
                                            {r.source}
                                        </span>
                                    </div>
                                    {r.title && (
                                        <h3
                                            className="font-display text-white mb-2"
                                            style={{ fontSize: 18, fontWeight: 500, lineHeight: 1.3 }}
                                        >
                                            {r.title}
                                        </h3>
                                    )}
                                    <blockquote
                                        className="font-body"
                                        style={{
                                            color: "var(--ls-text-secondary)",
                                            fontSize: 14,
                                            lineHeight: 1.65,
                                            flex: 1,
                                        }}
                                    >
                                        "{r.body}"
                                    </blockquote>
                                    <figcaption
                                        className="mt-5 text-xs"
                                        style={{ color: "var(--ls-text-muted)" }}
                                    >
                                        <strong className="text-white font-medium">{r.author_name}</strong>
                                        {r.author_location ? ` · ${r.author_location}` : ""}
                                    </figcaption>
                                </motion.figure>
                            ))}
                        </div>
                    )}
                </div>
            </section>

            {/* Submit review */}
            <section
                id="leave-a-review"
                style={{
                    background: "#070707",
                    borderTop: "1px solid var(--ls-border)",
                    borderBottom: "1px solid var(--ls-border)",
                }}
                data-testid="reviews-form-section"
            >
                <div className="ls-container py-20">
                    <div className="grid grid-cols-1 lg:grid-cols-12 gap-12">
                        <div className="lg:col-span-5">
                            <span className="eyebrow">Leave a Review</span>
                            <h2
                                className="font-display mt-4 text-white"
                                style={{
                                    fontSize: "clamp(32px, 4.5vw, 48px)",
                                    fontWeight: 500,
                                    lineHeight: 1.05,
                                    letterSpacing: "-0.02em",
                                }}
                            >
                                Tell us how we did.
                            </h2>
                            <p
                                className="mt-4"
                                style={{ color: "var(--ls-text-secondary)", fontSize: 16, lineHeight: 1.6 }}
                            >
                                On-site reviews are read by the owner and posted after a quick check.
                                Use <strong className="text-white">first name + last initial only</strong>{" "}
                                (e.g. <em>Sarah T.</em>) so it matches everyone else.
                            </p>
                        </div>

                        <div className="lg:col-span-7">
                            {submitState === "success" ? (
                                <div
                                    role="status"
                                    aria-live="polite"
                                    style={{ display: "flex", flexDirection: "column", gap: 18 }}
                                    data-testid="review-submit-success-wrap"
                                >
                                    <div
                                        style={{
                                            background: "rgba(245, 176, 39, 0.06)",
                                            border: "1px solid rgba(245, 176, 39, 0.4)",
                                            borderRadius: 8,
                                            padding: 36,
                                            display: "flex",
                                            gap: 16,
                                            alignItems: "flex-start",
                                        }}
                                        data-testid="review-submit-success"
                                    >
                                        <CheckCircle2 size={28} style={{ color: "var(--ls-gold)", flexShrink: 0 }} />
                                        <div>
                                            <h3
                                                className="font-display text-white"
                                                style={{ fontSize: 22, fontWeight: 500 }}
                                            >
                                                Thank you.
                                            </h3>
                                            <p
                                                className="mt-2 text-sm"
                                                style={{ color: "var(--ls-text-secondary)", lineHeight: 1.6 }}
                                            >
                                                {submitMessage}
                                            </p>
                                        </div>
                                    </div>

                                    {form.rating >= 4 && (
                                        <div
                                            style={{
                                                background:
                                                    "linear-gradient(135deg, rgba(231, 76, 60, 0.08) 0%, rgba(20, 20, 20, 1) 70%)",
                                                border: "1px solid rgba(231, 76, 60, 0.4)",
                                                borderRadius: 8,
                                                padding: 32,
                                            }}
                                            data-testid="review-google-cta-card"
                                        >
                                            <div className="flex gap-1 mb-3" aria-hidden="true">
                                                {[1, 2, 3, 4, 5].map((n) => (
                                                    <Star
                                                        key={n}
                                                        size={14}
                                                        fill="var(--ls-gold)"
                                                        stroke="var(--ls-gold)"
                                                    />
                                                ))}
                                            </div>
                                            <h3
                                                className="font-display text-white"
                                                style={{
                                                    fontSize: 26,
                                                    fontWeight: 500,
                                                    lineHeight: 1.2,
                                                }}
                                            >
                                                Love the Studio?{" "}
                                                <em
                                                    style={{
                                                        fontStyle: "italic",
                                                        color: "var(--ls-gold)",
                                                        fontWeight: 400,
                                                    }}
                                                >
                                                    Share it on Google.
                                                </em>
                                            </h3>
                                            <p
                                                className="mt-3 text-sm"
                                                style={{
                                                    color: "var(--ls-text-secondary)",
                                                    lineHeight: 1.6,
                                                }}
                                            >
                                                Your Google review helps other Clinton & Henry County
                                                locals find us — and it takes 30 seconds.
                                            </p>
                                            <a
                                                href={GOOGLE_REVIEW_URL}
                                                target="_blank"
                                                rel="noopener noreferrer"
                                                className="btn-primary mt-5"
                                                style={{ fontSize: 14, padding: "14px 26px" }}
                                                data-testid="review-google-cta-btn"
                                            >
                                                Leave a Google Review <ExternalLink size={13} />
                                            </a>
                                            <p
                                                className="mt-4 text-xs"
                                                style={{ color: "var(--ls-text-muted)" }}
                                            >
                                                Opens Google in a new tab. No account needed beyond your
                                                existing Google login.
                                            </p>
                                        </div>
                                    )}
                                </div>
                            ) : (
                                <form
                                    onSubmit={submitReview}
                                    style={{
                                        background: "var(--ls-panel)",
                                        border: "1px solid var(--ls-border)",
                                        borderRadius: 8,
                                        padding: 32,
                                        display: "flex",
                                        flexDirection: "column",
                                        gap: 18,
                                    }}
                                    aria-label="Submit a review"
                                    data-testid="review-form"
                                >
                                    {/* Honeypot */}
                                    <input
                                        type="text"
                                        tabIndex={-1}
                                        autoComplete="off"
                                        value={form.website}
                                        onChange={(e) => setForm({ ...form, website: e.target.value })}
                                        style={{ position: "absolute", left: "-9999px" }}
                                        aria-hidden="true"
                                    />

                                    {/* Rating */}
                                    <div>
                                        <label
                                            className="block text-sm font-medium text-white mb-2"
                                        >
                                            Rating <span style={{ color: "var(--ls-red)" }}>*</span>
                                        </label>
                                        <StarRow
                                            value={form.rating}
                                            size={28}
                                            interactive
                                            onChange={(n) => setForm({ ...form, rating: n })}
                                            testid="review-stars-input"
                                        />
                                        {formErrors.rating && (
                                            <p className="mt-2 text-xs" style={{ color: "var(--ls-red)" }}>
                                                {formErrors.rating}
                                            </p>
                                        )}
                                    </div>

                                    {/* Name + Location */}
                                    <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                                        <div>
                                            <label
                                                htmlFor="review-name"
                                                className="block text-sm font-medium text-white mb-2"
                                            >
                                                Name <span style={{ color: "var(--ls-red)" }}>*</span>
                                            </label>
                                            <input
                                                id="review-name"
                                                type="text"
                                                required
                                                placeholder="Sarah T."
                                                value={form.author_name}
                                                onChange={(e) => setForm({ ...form, author_name: e.target.value })}
                                                className="ls-input"
                                                data-testid="review-name-input"
                                            />
                                            <p className="mt-1.5 text-xs" style={{ color: "var(--ls-text-muted)" }}>
                                                First name + last initial only please.
                                            </p>
                                            {formErrors.author_name && (
                                                <p className="mt-1 text-xs" style={{ color: "var(--ls-red)" }}>
                                                    {formErrors.author_name}
                                                </p>
                                            )}
                                        </div>
                                        <div>
                                            <label
                                                htmlFor="review-location"
                                                className="block text-sm font-medium text-white mb-2"
                                            >
                                                Location <span style={{ color: "var(--ls-text-muted)" }}>(optional)</span>
                                            </label>
                                            <input
                                                id="review-location"
                                                type="text"
                                                placeholder="Clinton, MO"
                                                value={form.author_location}
                                                onChange={(e) => setForm({ ...form, author_location: e.target.value })}
                                                className="ls-input"
                                                data-testid="review-location-input"
                                            />
                                        </div>
                                    </div>

                                    {/* Title */}
                                    <div>
                                        <label
                                            htmlFor="review-title"
                                            className="block text-sm font-medium text-white mb-2"
                                        >
                                            Headline <span style={{ color: "var(--ls-text-muted)" }}>(optional)</span>
                                        </label>
                                        <input
                                            id="review-title"
                                            type="text"
                                            placeholder="Best bourbon shop in Henry County"
                                            value={form.title}
                                            onChange={(e) => setForm({ ...form, title: e.target.value })}
                                            className="ls-input"
                                            data-testid="review-title-input"
                                        />
                                    </div>

                                    {/* Body */}
                                    <div>
                                        <label
                                            htmlFor="review-body"
                                            className="block text-sm font-medium text-white mb-2"
                                        >
                                            Your Review <span style={{ color: "var(--ls-red)" }}>*</span>
                                        </label>
                                        <textarea
                                            id="review-body"
                                            required
                                            rows={6}
                                            minLength={20}
                                            maxLength={2000}
                                            placeholder="Tell us about your visit — what you bought, what the staff helped with, what you'd come back for…"
                                            value={form.body}
                                            onChange={(e) => setForm({ ...form, body: e.target.value })}
                                            className="ls-input"
                                            style={{ resize: "vertical", minHeight: 140 }}
                                            data-testid="review-body-input"
                                        />
                                        <p className="mt-1.5 text-xs" style={{ color: "var(--ls-text-muted)" }}>
                                            {form.body.length}/2000 — minimum 20 characters.
                                        </p>
                                        {formErrors.body && (
                                            <p className="mt-1 text-xs" style={{ color: "var(--ls-red)" }}>
                                                {formErrors.body}
                                            </p>
                                        )}
                                    </div>

                                    {submitState === "error" && (
                                        <p
                                            role="alert"
                                            className="text-sm"
                                            style={{ color: "var(--ls-red)" }}
                                            data-testid="review-submit-error"
                                        >
                                            {submitMessage}
                                        </p>
                                    )}

                                    <button
                                        type="submit"
                                        disabled={submitState === "loading"}
                                        className="btn-primary self-start mt-2"
                                        style={{ padding: "12px 28px", fontSize: 14 }}
                                        data-testid="review-submit-btn"
                                    >
                                        {submitState === "loading" ? (
                                            <>
                                                <Loader2 size={14} className="animate-spin" /> Submitting…
                                            </>
                                        ) : (
                                            "Submit Review"
                                        )}
                                    </button>
                                    <p className="text-xs" style={{ color: "var(--ls-text-muted)" }}>
                                        Reviews are moderated and typically appear within 1 business day.
                                        21+ only.
                                    </p>
                                </form>
                            )}
                        </div>
                    </div>
                </div>
            </section>
        </>
    );
}
